import Description from '@/_components/template/Description'
import GotoEndpoint from '@/_components/template/GotoEndpoint'
import Heading from '@/_components/template/Heading'
import SectionBlock from '@/_components/template/SectionBlock'
import SectionHeading from '@/_components/template/SectionHeading'
import { PROJECT_DETAILS } from '@/content/portfolioContent'
import { usePageSeo } from '@/lib/seo'
import { Link, Navigate, useParams } from 'react-router-dom'

export default function ProjectDetailPage() {
    const { projectId } = useParams()
    const project = projectId ? PROJECT_DETAILS[projectId] : undefined

    if (!project) {
        return <Navigate to="/" replace />
    }

    usePageSeo({
        title: `${project.name} | Tuhin Kairi Case Study`,
        description: project.summary,
        type: 'article',
        keywords: [project.name, project.category, project.role, ...project.tags, ...project.stack],
        jsonLd: {
            '@context': 'https://schema.org',
            '@type': 'CreativeWork',
            headline: project.name,
            description: project.summary,
            about: project.category,
            keywords: [...project.tags, ...project.stack].join(', '),
            author: {
                '@type': 'Person',
                name: 'Tuhin Kairi',
            },
        },
    })

    return (
        <section className="relative tracking-wider sm:px-6">
            <main id="project-top" className="container mx-auto min-h-[calc(100vh-10rem)] px-4 pt-32 pb-20 sm:px-5 sm:pt-36 lg:px-0 lg:pt-20">
                {/* <div className="mb-8 flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.22em] text-secondary">
                    <Link to="/" className="hover:text-primary">Home</Link>
                    <span>/</span>
                    <span>{project.category}</span>
                    <span>/</span>
                    <span className="text-primary">{project.name}</span>
                </div> */}

                <SectionBlock className="grid-cols-1 gap-8 border-b border-border py-12 lg:grid-cols-[1.15fr_0.85fr]">
                    <div className="space-y-6 text-center lg:text-left">
                        <SectionHeading number="01" content={project.category} className="justify-center pb-2 lg:justify-start" />
                        <Heading className="mx-auto max-w-[14ch] lg:mx-0">{project.name}</Heading>
                        <Description className="mx-auto max-w-3xl leading-7 text-secondary sm:text-lg lg:mx-0 lg:w-full">
                            {project.summary}
                        </Description>
                        <p className="mx-auto max-w-3xl text-base leading-7 text-primary sm:text-lg lg:mx-0">{project.heroNote}</p>
                    </div>

                    <div className="rounded-[2rem] border border-border bg-surface p-6 sm:p-8">
                        <p className="text-xs font-semibold uppercase tracking-[0.35em] text-secondary">Project Snapshot</p>
                        <div className="mt-6 grid gap-4 text-sm text-primary sm:grid-cols-2 sm:text-base">
                            <div>
                                <p className="uppercase tracking-[0.2em] text-secondary">Role</p>
                                <p className="mt-2">{project.role}</p>
                            </div>
                            <div>
                                <p className="uppercase tracking-[0.2em] text-secondary">Status</p>
                                <p className="mt-2">{project.status}</p>
                            </div>
                            <div>
                                <p className="uppercase tracking-[0.2em] text-secondary">Duration</p>
                                <p className="mt-2">{project.duration}</p>
                            </div>
                            <div>
                                <p className="uppercase tracking-[0.2em] text-secondary">Reading Time</p>
                                <p className="mt-2">{project.readingTime}</p>
                            </div>
                        </div>
                        <div className="mt-6 flex flex-wrap gap-2">
                            {project.tags.map((tag) => (
                                <span key={tag} className="rounded-full border border-border px-3 py-1.5 text-xs uppercase tracking-[0.15em] text-primary sm:text-sm">
                                    {tag}
                                </span>
                            ))}
                        </div>
                    </div>
                </SectionBlock>

                <SectionBlock className="grid-cols-1 items-start gap-8 border-t border-border py-12 lg:grid-cols-[0.33fr_0.67fr]">
                    <aside className="_sectionNav lg:sticky top-24 h-fit self-start sm:top-28 lg:top-28">
                        <div className="max-h-[calc(100vh-7rem)] space-y-6 overflow-y-auto pr-1 sm:max-h-[calc(100vh-8rem)] lg:max-h-[calc(100vh-8rem)]">
                        <div className="rounded-[2rem] border border-border bg-surface p-6">
                            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-secondary">Section Nav</p>
                            <div className="mt-5 flex flex-col gap-3">
                                {project.sections.map((section) => (
                                    <Link
                                        key={section.id}
                                        to={`#${section.id}`}
                                        className="text-sm uppercase tracking-[0.18em] text-secondary underline underline-offset-4 hover:text-primary"
                                    >
                                        {section.title}
                                    </Link>
                                ))}
                            </div>
                        </div>

                        <div className="rounded-[2rem] border border-border bg-surface p-6">
                            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-secondary">Direct Links</p>
                            <div className="mt-5 flex flex-col gap-3">
                                {project.links.map((link) => (
                                    <a
                                        key={link.label}
                                        href={link.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-sm uppercase tracking-[0.18em] text-primary underline underline-offset-4 hover:text-secondary"
                                    >
                                        {link.label}
                                    </a>
                                ))}
                            </div>
                        </div>
                        </div>
                    </aside>

                    <div className="space-y-8">
                        <div className="rounded-[2rem] border border-border bg-ground p-6 sm:p-8">
                            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-secondary">Key Metrics</p>
                            <div className="mt-5 grid gap-4 md:grid-cols-3">
                                {project.keyMetrics.map((metric) => (
                                    <div key={metric.label} className="rounded-[1.5rem] border border-border bg-surface p-5">
                                        <p className="text-xs uppercase tracking-[0.18em] text-secondary">{metric.label}</p>
                                        <p className="mt-3 text-lg text-primary">{metric.value}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {project.sections.map((section, index) => (
                            <article key={section.id} id={section.id} className="rounded-[2rem] border border-border bg-ground p-6 sm:p-8">
                                <div className="flex flex-col gap-4 border-b border-border pb-6 lg:flex-row lg:items-end lg:justify-between">
                                    <div>
                                        <p className="text-xs font-semibold uppercase tracking-[0.35em] text-secondary">
                                            {String(index + 1).padStart(2, '0')}
                                        </p>
                                        <h2 className="mt-3 text-3xl font-heading text-primary sm:text-4xl">{section.title}</h2>
                                    </div>
                                    <Link to="#project-top" className="text-sm uppercase tracking-[0.18em] text-secondary underline underline-offset-4 hover:text-primary">
                                        Back to top
                                    </Link>
                                </div>

                                <Description className="mb-0 mt-6 w-full max-w-none leading-7 text-secondary sm:text-lg">
                                    {section.intro}
                                </Description>

                                <div className="mt-6 grid gap-4">
                                    {section.highlights.map((item) => (
                                        <div key={`${section.id}-${item.label}`} className="rounded-[1.5rem] border border-border bg-surface p-5">
                                            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-secondary">{item.label}</p>
                                            <p className="mt-3 text-base leading-7 text-primary sm:text-lg">{item.detail}</p>
                                        </div>
                                    ))}
                                </div>

                                {section.mermaid ? (
                                    <div className="mt-6 rounded-[1.5rem] border border-border bg-surface p-5">
                                        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-secondary">Mermaid Diagram</p>
                                        <pre className="mt-4 overflow-x-auto whitespace-pre-wrap text-sm leading-7 text-primary">
{section.mermaid}
                                        </pre>
                                    </div>
                                ) : null}
                            </article>
                        ))}
                    </div>
                </SectionBlock>

                <SectionBlock className="grid-cols-1 gap-8 border-t border-border py-12">
                    <div className="text-center lg:text-left">
                        <SectionHeading number="03" content="Backlinks" className="justify-center pb-4 lg:justify-start" />
                        <Heading className="mx-auto max-w-[12ch] lg:mx-0">Return to the related portfolio section</Heading>
                    </div>
                    <div className="flex flex-wrap justify-center gap-3 lg:justify-start">
                        {project.relatedLinks.map((link) => (
                            <Link
                                key={link.label}
                                to={link.url}
                                className="rounded-full border border-border px-4 py-2 text-sm uppercase tracking-[0.18em] text-primary transition-colors hover:border-primary hover:text-primary"
                            >
                                {link.label}
                            </Link>
                        ))}
                    </div>
                </SectionBlock>

                <div className="pt-6 text-center lg:text-left">
                    <GotoEndpoint content="back to home" endpoint="/#feature-work" className="inline-flex items-center sm:text-lg" />
                </div>
            </main>
        </section>
    )
}
